import { useState, useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Navbar } from "@/components/custom/Navbar";
import { Footer } from "@/components/custom/Footer";
import { HelmetProvider } from "react-helmet-async";
import { Loader2 } from "lucide-react";

// Lazy Loaded Pages - Mapping named exports to default for React.lazy
const ComparadorPage = lazy(() => import("@/components/custom/ComparadorPage"));
const HomePage = lazy(() => {
  console.log("HomePage rendering...");
  return import("@/components/custom/HomePage");
});
const TarifasPage = lazy(() => import("@/components/custom/TarifasPage").then(m => ({ default: m.TarifasPage })));
const HistoricasPage = lazy(() => import("@/components/custom/HistoricasPage").then(m => ({ default: m.HistoricasPage })));
const VehiculosPage = lazy(() => import("@/components/custom/VehiculosPage").then(m => ({ default: m.VehiculosPage })));
const FAQPage = lazy(() => import("@/components/custom/FAQPage").then(m => ({ default: m.FAQPage })));
const AdminPage = lazy(() => import("@/components/custom/AdminPage").then(m => ({ default: m.AdminPage })));
const InversionPage = lazy(() => import("@/components/custom/InversionPage").then(m => ({ default: m.InversionPage })));
const TIRPage = lazy(() => import("@/components/custom/TIRPage").then(m => ({ default: m.TIRPage })));
const DashboardTop10 = lazy(() => import("@/components/custom/DashboardTop10").then(m => ({ default: m.DashboardTop10 })));
const BlogPage = lazy(() => import("@/components/custom/BlogPage").then(m => ({ default: m.BlogPage })));
const BlogPostPage = lazy(() => import("@/components/custom/BlogPostPage").then(m => ({ default: m.BlogPostPage })));
const PreciosPage = lazy(() => import("@/components/custom/PreciosPage").then(m => ({ default: m.PreciosPage })));
const EstadisticasPage = lazy(() => import("@/components/custom/EstadisticasPage").then(m => ({ default: m.EstadisticasPage })));
const EstadisticasPreciosPage = lazy(() => import("@/components/custom/EstadisticasPreciosPage").then(m => ({ default: m.EstadisticasPreciosPage })));
const PredictorRutaPage = lazy(() => import("@/components/custom/PredictorRutaPage").then(m => ({ default: m.PredictorRutaPage })));
const TCOProPage = lazy(() => import("@/components/custom/TCOProPage").then(m => ({ default: m.TCOProPage })));
const BatteryHealthPage = lazy(() => import("@/components/custom/BatteryHealthPage").then(m => ({ default: m.BatteryHealthPage })));
const FleetStatsPage = lazy(() => import("@/components/custom/FleetStatsPage"));
const CreditosCarbonoPage = lazy(() => import("@/components/custom/CreditosCarbonoPage").then(m => ({ default: m.CreditosCarbonoPage })));

// Scroll to top on route change, but preserve scroll on POP (back/forward)
function ScrollToTop() {
  const { pathname } = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    if (navType !== "POP") {
      window.scrollTo(0, 0);
    }
  }, [pathname, navType]);

  return null;
}

// Fallback loader component
const PageLoader = () => (
  <div className="flex-1 flex items-center justify-center min-h-[50vh]">
    <Loader2 className="h-8 w-8 animate-spin text-primary/40" />
  </div>
);

// Theme context provider
export const ThemeContext = ({ children }) => {
  const [darkMode, setDarkMode] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const stored = localStorage.getItem("darkMode");
    if (stored) {
      setDarkMode(stored === "true");
    }
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("darkMode", darkMode);
  }, [darkMode]);

  return (
    <div className="min-h-screen flex flex-col transition-colors duration-300">
      <Navbar darkMode={darkMode} setDarkMode={setDarkMode} />
      <main className={`${location.pathname === "/" ? "pt-0" : "pt-4"} flex-1 flex flex-col`}>
        <Suspense fallback={<PageLoader />}>
          {children}
        </Suspense>
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App min-h-screen bg-background">
      <HelmetProvider>
        <BrowserRouter>
          <ScrollToTop />
          <ThemeContext>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/comparador" element={<ComparadorPage />} />
              <Route path="/tarifas" element={<TarifasPage />} />
              <Route path="/tarifas/historicas" element={<HistoricasPage />} />
              <Route path="/vehiculos" element={<VehiculosPage />} />
              <Route path="/inversion" element={<InversionPage />} />
              <Route path="/tir" element={<TIRPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/top-eficiencia" element={<DashboardTop10 />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/precios" element={<PreciosPage />} />
              <Route path="/estadisticasparque" element={<FleetStatsPage />} />
              <Route path="/estadisticas" element={<EstadisticasPage />} />
              <Route path="/estadisticas-precios" element={<EstadisticasPreciosPage />} />
              <Route path="/calculadora/ruta" element={<PredictorRutaPage />} />
              <Route path="/tco-avanzado" element={<TCOProPage />} />
              <Route path="/salud-bateria" element={<BatteryHealthPage />} />
              <Route path="/creditos-carbono" element={<CreditosCarbonoPage />} />
            </Routes>
          </ThemeContext>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </HelmetProvider>
    </div>
  );
}

export default App;



